import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { User } from '../models/user.model';
import { environment } from '../../environments/environment';
import { map, mergeMap } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NgxPermissionsService } from 'ngx-permissions';
import { MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { OAuthService } from 'angular-oauth2-oidc';
const helper = new JwtHelperService();
import { Router } from '@angular/router';
import { teamConfig } from '../login/sso.config';
import { AuthenticationResult, InteractionRequiredAuthError, InteractionType, RedirectRequest } from '@azure/msal-browser';
import { HttpHeaders, HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _currentUser: BehaviorSubject<User>;
  private tokenRefreshWorker!: Worker;

  constructor(
    private http: HttpClient,
    private ngxPermissionsService: NgxPermissionsService,
    private msalService: MsalService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private router: Router,
    private oauthService: OAuthService,
    public jwtHelper: JwtHelperService,
    ) {
      this._currentUser = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('user')));
  }


  async generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
 
  const digest = await window.crypto.subtle.digest('SHA-256', data);
 
  // Convert ArrayBuffer to base64-url string
  const hashArray = Array.from(new Uint8Array(digest));
  const base64 = btoa(String.fromCharCode(...hashArray));
 
  // Convert to base64url format
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

  public isAuthenticated(): boolean {
    const currentUser = JSON.parse(localStorage.getItem('user'));
    if (!currentUser) return false;
    return !helper.isTokenExpired(currentUser.token);
  }

  get currentUser(): User {
    return this._currentUser.value;
  }

  getRolSo(roles) {
    const rls = [];
    let so = [];

    roles.sort().forEach(rol => {
      const r = rol.split('_');
      if (r.length > 0) {
        rls.push(r[0].toLowerCase());

        if (r[1]) {
          so.push(r[1]);
        }
      }
    });

    return { rls, so };
  }

  login(type, authResponse = null) {
    let userStorage: User;

    if (type === 'AD') {
      const rolSo = this.getRolSo(authResponse.idTokenClaims.roles);
      userStorage = {
        name: authResponse.account.name,
        username: authResponse.account.userName,
        roles: rolSo.rls,
        so: rolSo.so,
        token: authResponse.idToken,
        exp: authResponse.idTokenClaims?.exp,
        provider: 'AD',
        authPayload: authResponse
      };
    }
    else {
      //const adUsername = `SSN\\${authResponse['data']['nickname']}`;
      const decodedIdToken = authResponse?.id_token ? this.jwtHelper.decodeToken(authResponse.id_token) : null;
      const expiresIn = Number(authResponse?.expires_in ?? 0);
      const expiresAtFromToken = decodedIdToken?.exp ? Number(decodedIdToken.exp) * 1000 : null;
      const adUsername = decodedIdToken?.nickname ? `SSN\\${decodedIdToken.nickname}` : null;
      const fullName = decodedIdToken?.name
        ? decodedIdToken.name
        : `${decodedIdToken?.given_name ?? ''} ${decodedIdToken?.family_name ?? ''}`.trim();

      userStorage = {
        //name: `${authResponse['data']['given_name']} ${authResponse['data']['family_name']}`,
        //username: authResponse['data']['nickname'],
        //token: authResponse['token']['id_token'],
        //accessToken: authResponse['token']['access_token'],
        //exp: authResponse['token']['expires_in'],
        name: fullName,
        username: decodedIdToken?.nickname,
        roles: ['gip_client'],
        token: authResponse?.id_token,
        accessToken: authResponse?.access_token,
        exp: authResponse?.expires_in,
        provider: 'TEAM',
        ad_username: adUsername,
        authPayload: authResponse,
        email: decodedIdToken?.website ?? decodedIdToken?.nickname,
        expiresAt: expiresAtFromToken ?? (expiresIn > 0 ? new Date(new Date().toUTCString()).getTime() + expiresIn * 1000 : null)
      };
    }

    if (!this.checkRol(userStorage.roles[0])) {
      this.logout(type === 'AD' ? 'AD' : 'TEAM');
      return of(false);
    }

    this.ngxPermissionsService.loadPermissions(userStorage.roles);

    localStorage.setItem('user', JSON.stringify(userStorage));
    this._currentUser.next(userStorage);
    this.clearStores('invoicesFilters');
    return of(this.isAuthenticated());
  }

  setUserStorage(session) {
    if (session.idToken && session.idToken.rawIdToken === this.currentUser.token) return;
    const user = {
      ...this.currentUser,
      exp: session.idTokenClaims.exp,
      authPayload: session,
      token: session.idToken
    }
    localStorage.setItem('user', JSON.stringify(user));
     localStorage.setItem('token_expiresOn', session.expiresOn?.toISOString() ?? "");
    this._currentUser.next(user);
  }

  getUserInfo(token: any) {
  const headers = new HttpHeaders({
    Authorization: `Bearer ${token.access_token}` //,
    //'Content-Type': 'application/x-www-form-urlencoded'
  });

  return this.http
    .post(`${environment.team.teamUrl}/userinfo`, {}, { headers })
    .pipe(
      map(data => ({ data, token }))
    );
}

teamAuth(params: any) {
  const body = {
    client_id: environment.team.clientId,
    redirect_uri: window.location.origin,
    scope: teamConfig.scope,
    code: params['code'],
    code_verifier: sessionStorage.getItem('c_ver'),
    grant_type: teamConfig.grantType
  };

  // Solo agregar X-Token-Issuer si la URL es la API interna (environment.apiURL)
  const url = `${environment.team.teamUrl}/oauth/token`;
  let apiURL = environment.apiUrl;
  let headers = { headers: { 'Content-Type': 'application/json' } };

  return this.http
    .post(url, body, headers)
    .pipe(
      map(data => {
        sessionStorage.setItem('team-token', JSON.stringify(data));
        return data;
      }),
      //mergeMap(token => this.getUserInfo(token))
    );

}

  checkRol(rol) {
    switch (rol) {
      case 'analyst':
      case 'user':
      case 'visu':
      case 'visupartial':
      case 'commercial':
      case 'bpo':
      case 'gip_client':
        return true;
      default:
        return false;
    }
  }
  
  logout(prov: string = null) {
    const provider = prov || (this.currentUser && this.currentUser.provider ? this.currentUser.provider : '');
    
    this.clearStores();
    this._currentUser.next(null);

    if ([provider, prov].includes('AD')) {
     if (this.msalGuardConfig.interactionType === InteractionType.Popup) 
    {
      this.msalService.logoutPopup({
        postLogoutRedirectUri: "/",
        mainWindowRedirectUri: "/"
      });
    } 
    else 
    {
      this.msalService.logoutRedirect({
        postLogoutRedirectUri: "/",
      });
    }
    }
    else {
      this.ngxPermissionsService.flushPermissions();
      window.location.href = environment.team.logoutUrl;
    }
    return of(true);
  }

  clearStores(key: string = null) {
    if (key) {
      localStorage.removeItem(key);
    }
    else {
      localStorage.removeItem('user');
      localStorage.removeItem('invoicesFilters');
      localStorage.removeItem('token_expiresOn');
      sessionStorage.removeItem('team-token');
    }
  }

  usingIe() {
    var ua = window.navigator.userAgent;
    var msie = ua.indexOf("MSIE ");

    if (msie > 0 || !!navigator.userAgent.match(/Trident.*rv\:11\./)) {
      return true;
    }
    else {
      return false;
    }
  }

  loginRedirectAd() {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }

/**
 * Verifica si el token es válido y, si está vencido, lo renueva silenciosamente.
 * Devuelve el nuevo token si fue exitoso, o null si falló.
 */
public async verifyAndRenewToken(): Promise<string | null> {
  const accounts = this.msalService.instance.getAllAccounts();

  if (accounts.length === 0) {
    console.warn('⚠️ No se encontraron cuentas activas.');
    return null;
  }

  const tokenExpiresOn = this.getTokenExpirationDate();
  const now = new Date();

  // Si el token sigue vigente
  if (tokenExpiresOn && tokenExpiresOn > now) {
    console.log('✅ Token aún válido, sin necesidad de renovación');
    this.restartTokenRefreshWorker();
    return this.currentUser?.token ?? null;
  }

  // Si está vencido, intenta refrescar
  try {
    const success = await firstValueFrom(this.refreshTokenv2(accounts[0].username));

    if (success) {
      console.log('🔄 Token renovado correctamente');
      this.restartTokenRefreshWorker();

      // Aseguramos devolver el nuevo token actualizado
      const newUser = this.currentUser;
      return newUser?.token ?? null;
    } else {
      console.error('❌ Error durante la renovación del token (success=false)');
      return null;
    }
  } catch (error) {
    console.error('❌ Error durante verifyAndRenewToken:', error);
    return null;
  }
}


   public refreshTokenv2(username: string): Observable<boolean> {
  const result = new ReplaySubject<boolean>(1); // ✅ evita pérdida de emisión

  const account = this.msalService.instance.getAllAccounts()[0];
  const accessTokenRequest = {
    scopes: ['user.read'],
    account,
    forceRefresh: true
  };

  this.msalService.instance.acquireTokenSilent(accessTokenRequest)
    .then((response: AuthenticationResult) => {
      this.msalService.instance.setActiveAccount(response.account);
      this.setUserStorage(response);
      this.restartTokenRefreshWorker();
      result.next(true);
      result.complete();
    })
    .catch((error: any) => {
      console.log('error catch refreshTokenv2', error);
      if (error instanceof InteractionRequiredAuthError) {
        this.msalService.instance.acquireTokenRedirect(accessTokenRequest);
      }
      result.next(false);
      result.complete();
    });

  return result.asObservable();
}


   private restartTokenRefreshWorker() {
    const tokenExpiresOn = this.getTokenExpirationDate();

    // Verificar si hay una fecha de caducidad válida
    if (tokenExpiresOn) {
      // Inicializar o reiniciar el Web Worker según sea necesario
      if (!this.tokenRefreshWorker) {
        // Inicializar el Web Worker
        this.startTokenRefreshWorker(tokenExpiresOn);
      }
      else {
        // Reiniciar el Web Worker con la nueva fecha de caducidad
        this.tokenRefreshWorker.postMessage({ type: 'start', tokenExpiresOn: tokenExpiresOn });
      }
    }
  }

async startTokenRefreshWorker(tokenExpiresOn: Date) {
    if (typeof Worker !== 'undefined') {
      try {
        this.tokenRefreshWorker = new Worker(new URL('../workers/authentication/token-refresh.worker', import.meta.url), { type: 'module' });

        this.tokenRefreshWorker.addEventListener('message', async ({ data }) => {
          if (data === 'refresh') {
            const accounts = this.msalService.instance.getAllAccounts();
            if (accounts.length === 0 || !accounts[0].username) {
              console.error('Cannot refresh token: no account or username available');
              return;
            }
            try {
              const success = await this.refreshTokenv2(accounts[0].username).toPromise();
              if (success) {
                console.log('Token refreshed successfully via Web Worker');
              } else {
                console.error('Error during token refresh: refreshTokenv2 returned false');
              }
            } catch (error) {
              console.error('Error during token refresh in Web Worker:', error);
            }
          }
        });

        this.tokenRefreshWorker.postMessage({ type: 'start', tokenExpiresOn });
        console.log('Web Worker started with expiration:', tokenExpiresOn);
      } catch (error) {
        console.error('Error initializing Web Worker:', error);
      }
    } else {
      console.error('Web Workers are not supported in this environment');
    }
  }

    public getTokenExpirationDate(): Date | null {
    const dateStr = localStorage.getItem('token_expiresOn');
    if (dateStr) {
      return new Date(dateStr);
    } else {
      return null
    }

  }

   terminateTokenRefreshWorker() {
    if (this.tokenRefreshWorker) {
      this.tokenRefreshWorker.terminate();
      this.tokenRefreshWorker = null;
    }
  }
}